'use client';

import { c as _c } from "react/compiler-runtime";
import { BREAKPOINTS, MEDIA_QUERIES } from '@repo/panda-config/breakpoints';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectIsMobileViewport, updateCompactViewport, updateMobileViewport } from '@repo/store/maps/slices/clientContext';
import { useCallback, useEffect, useRef } from 'react';
const DEBOUNCE_DELAY = 200; // Increased for better performance
const ORIENTATION_DELAY = 100; // Shorter delay for orientation changes

// Hook for setting up mobile viewport detection listener (use only once at top level)
export const useMobileViewportDetection = () => {
  const $ = _c(8);
  const dispatch = useAppDispatch();
  const isMobileViewport = useAppSelector(selectIsMobileViewport);
  const lastValueRef = useRef(isMobileViewport);
  const lastCompactRef = useRef(false);
  const resizeTimeoutRef = useRef(null);
  const orientationTimeoutRef = useRef(null);
  let t0;
  if ($[0] !== dispatch) {
    t0 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const isMobileViewportSize = window.innerWidth <= BREAKPOINTS.mobile;
      const isSmallWindow = window.innerWidth <= BREAKPOINTS.tablet && window.innerHeight <= BREAKPOINTS.maxHeightSmallWindow;
      const newIsMobileViewport = isMobileViewportSize || isSmallWindow;
      if (lastValueRef.current !== newIsMobileViewport) {
        lastValueRef.current = newIsMobileViewport;
        dispatch(updateMobileViewport(newIsMobileViewport));
      }
    };
    $[0] = dispatch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const checkMobileViewport = t0;
  let t1;
  let t2;
  if ($[2] !== dispatch) {
    t1 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const mediaQuery = window.matchMedia(MEDIA_QUERIES.compact);
      const handleChange = e => {
        if (lastCompactRef.current !== e.matches) {
          lastCompactRef.current = e.matches;
          dispatch(updateCompactViewport(e.matches));
        }
      };
      lastCompactRef.current = mediaQuery.matches;
      dispatch(updateCompactViewport(mediaQuery.matches));
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    };
    t2 = [dispatch];
    $[2] = dispatch;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== checkMobileViewport) {
    t3 = () => {
      checkMobileViewport();
      const handleResize = () => {
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }
        resizeTimeoutRef.current = window.setTimeout(checkMobileViewport, DEBOUNCE_DELAY);
      };
      const handleOrientationChange = () => {
        if (orientationTimeoutRef.current) {
          clearTimeout(orientationTimeoutRef.current);
        }
        orientationTimeoutRef.current = window.setTimeout(checkMobileViewport, ORIENTATION_DELAY);
      };
      window.addEventListener("resize", handleResize, {
        passive: true
      });
      window.addEventListener("orientationchange", handleOrientationChange, {
        passive: true
      });
      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("orientationchange", handleOrientationChange);
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }
        if (orientationTimeoutRef.current) {
          clearTimeout(orientationTimeoutRef.current);
        }
      };
    };
    t4 = [checkMobileViewport];
    $[5] = checkMobileViewport;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  return isMobileViewport;
};